import { useGetNationalityList } from "@/api/services/global/master";
import { InnerBox } from "@/components/elements/InnerBox";
import { YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { findLabelByValue } from "@/utils/object";
import { Stack, Typography, styled, Collapse } from "@mui/material";
import dayjs from "dayjs";
import { TWorkExperience } from "@/api/entities/talent.entity";

type TTalentWorkExperienceProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentWorkExperience: FCC<TTalentWorkExperienceProps> = ({ defaultValues }) => {
  const { nationalityOptions } = useGetNationalityList();
  const { expanded, toggle } = useCollapsible();

  const workExperiences = defaultValues?.workExperiences || [];
  const hasMultipleExperiences = workExperiences.length > 1;

  const getCountryName = (exp: TWorkExperience) => {
    if (exp.countryName) {
      return `(${exp.countryName})`;
    }
    if (exp.countryId) {
      return `(${findLabelByValue(nationalityOptions, exp.countryId)})`;
    }
    return "";
  };
  return (
    <>
      <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
        <CollapsibleSectionHeader title="職歴" expanded={expanded} onToggle={toggle} />

        <Collapse in={expanded}>
          {workExperiences.length
            ? workExperiences.map((item, index) => (
                <Stack mt={3} key={index}>
                  <Stack p={2} border={`1px solid ${black[99]}`} borderRadius={rounded.sm}>
                    {hasMultipleExperiences && (
                      <Typography variant="body14Bold" color={black[30]} sx={{ mb: 1 }}>
                        職歴{index + 1}
                      </Typography>
                    )}
                    <Typography
                      variant="body14"
                      color={black[30]}
                      sx={{
                        mb: 0.5,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {item.jobType ?? EMPTY_STR.TEXT}
                    </Typography>
                    <Typography
                      variant="body14"
                      color={black[30]}
                      sx={{
                        mb: 0.5,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {item.companyName ?? EMPTY_STR.TEXT} {getCountryName(item)}
                    </Typography>
                    <Typography variant="cap12" color={black[70]}>
                      {item?.startDate && dayjs(item.startDate).isValid() ? dayjs(item.startDate).format(YEAR_MONTH_STR_FORMAT) : ""}
                      {item?.startDate && dayjs(item.startDate).isValid() && item?.endDate && dayjs(item.endDate).isValid() ? "～" : ""}
                      {item?.endDate && dayjs(item.endDate).isValid() ? dayjs(item.endDate).format(YEAR_MONTH_STR_FORMAT) : ""}
                    </Typography>
                  </Stack>
                </Stack>
              ))
            : null}
        </Collapse>
      </InnerBox>
    </>
  );
};

export const WrappableTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
`;

export const ClampedTypography = styled(Typography)<{ lines?: number }>`
  word-break: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines = 2 }) => lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const FlexWrapTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  flex: 1;
  min-width: 0;
`;
